import { Button, Card, Chip, Seg, faDigits } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type OrderDropAlertRow } from "../../api.js";
import { ErrorLine, useAction } from "../common.js";

type AlertFilter = "unreviewed" | "reviewed" | "all";

export function AlertsTab() {
  const [filter, setFilter] = useState<AlertFilter>("unreviewed");
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [reviewNote, setReviewNote] = useState("");
  const [reviewedLocally, setReviewedLocally] = useState<
    Map<string, { reviewedAt: string; reviewNote: string | null; original: OrderDropAlertRow }>
  >(new Map());

  const alerts = useQuery({
    queryKey: ["forecast", "alerts", filter],
    queryFn: () => api<OrderDropAlertRow[]>(`/forecast/alerts?filter=${filter}`),
  });

  const reviewAction = useAction(
    async ({ alert, note }: { alert: OrderDropAlertRow; note: string }) => {
      const trimmed = note.trim() ? note.trim() : "بررسی شد";
      await api(`/forecast/alerts/${alert.id}/review`, {
        body: { note: trimmed },
      });
      setReviewedLocally((prev) => {
        const next = new Map(prev);
        next.set(alert.id, {
          reviewedAt: new Date().toISOString(),
          reviewNote: trimmed,
          original: alert,
        });
        return next;
      });
      setReviewingId(null);
      setReviewNote("");
    },
    [["forecast", "alerts"], ["manager-dashboard"]],
  );

  const detectAction = useAction(async () => {
    await api("/forecast/alerts/detect", { body: {} });
  }, [["forecast", "alerts"], ["manager-dashboard"]]);

  const serverList = alerts.data ?? [];
  const list: OrderDropAlertRow[] = serverList.map((a) => {
    const local = reviewedLocally.get(a.id);
    return local ? { ...a, reviewedAt: local.reviewedAt, reviewNote: local.reviewNote } : a;
  });

  if (filter === "unreviewed") {
    for (const [id, local] of reviewedLocally.entries()) {
      if (!list.some((a) => a.id === id)) {
        list.push({ ...local.original, reviewedAt: local.reviewedAt, reviewNote: local.reviewNote });
      }
    }
  }

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Header & Filter Controls */}
      <div className="row wrap" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <Seg<AlertFilter>
          label="فیلتر وضعیت هشدارها"
          value={filter}
          onChange={setFilter}
          options={[
            { value: "unreviewed", label: "هشدارهای باز" },
            { value: "reviewed", label: "تاریخچه بررسی‌شده‌ها" },
            { value: "all", label: "همه هشدارها" },
          ]}
        />

        <Button
          type="button"
          onClick={() => {
            detectAction.run({});
          }}
          disabled={detectAction.busy}
        >
          {detectAction.busy ? "در حال پردازش..." : "ارزیابی مجدد سفارش‌ها"}
        </Button>
      </div>

      <ErrorLine error={reviewAction.error || detectAction.error} />

      {alerts.isLoading && <p className="ink2">در حال بارگذاری هشدارها...</p>}
      {alerts.error && (
        <p className="hint" role="alert" style={{ color: "var(--coral-ink)" }}>
          خطا در دریافت هشدارها: {alerts.error.message}
        </p>
      )}

      {!alerts.isLoading && list.length === 0 && (
        <Card>
          <p className="ink2" style={{ textAlign: "center", padding: "var(--s3) 0" }}>
            {filter === "unreviewed"
              ? "هیچ هشدار افت سفارشی در حال حاضر وجود ندارد. همه چیز مطابق معمول است."
              : "موردی برای نمایش یافت نشد."}
          </p>
        </Card>
      )}

      {/* Alerts List */}
      <div className="stack" style={{ gap: "var(--s3)" }}>
        {list.map((alert) => {
          const isReviewing = reviewingId === alert.id;
          const isCritical = alert.severity === "critical";

          return (
            <Card key={alert.id}>
              <div className="stack" style={{ gap: "var(--s2)" }}>
                <div className="row wrap" style={{ justifyContent: "space-between", alignItems: "center" }}>
                  <div className="row wrap" style={{ gap: "var(--s2)", alignItems: "center" }}>
                    <Chip tone={isCritical ? "coral" : "warn"}>
                      {isCritical ? "افت شدید (بحرانی)" : "افت سفارش (مهم)"}
                    </Chip>
                    <strong style={{ fontSize: "1.1rem" }}>
                      {alert.organizationName
                        ? `${alert.organizationName} — ${alert.unitName ?? alert.unitCode ?? alert.unitId}`
                        : (alert.unitName ?? alert.unitCode ?? alert.unitId)}
                    </strong>
                  </div>

                  <span className="hint ink2">تاریخ تشخیص: {faDigits(alert.detectedDate)}</span>
                </div>

                <div
                  className="row wrap"
                  style={{
                    gap: "var(--s3)",
                    background: "var(--surface2, #f8fafc)",
                    padding: "var(--s2)",
                    borderRadius: "6px",
                  }}
                >
                  <div>
                    <span className="hint">میزان افت: </span>
                    <strong
                      className="num"
                      style={{ color: isCritical ? "var(--coral-ink)" : "var(--amber-ink)" }}
                    >
                      {faDigits((alert.dropPerMille / 10).toFixed(1))}%
                    </strong>
                  </div>
                  <div>
                    <span className="hint">میانگین اخیر: </span>
                    <strong className="num">{faDigits(alert.recentAveragePortions)} پرس</strong>
                  </div>
                  <div>
                    <span className="hint">سفارش معمول گذشته: </span>
                    <strong className="num">{faDigits(alert.usualPortions)} پرس</strong>
                  </div>
                  <div>
                    <span className="hint">نمونه‌های اخیر: </span>
                    <span className="num ink2">
                      [{alert.recentSamples.map((s) => faDigits(s)).join("، ")}]
                    </span>
                  </div>
                </div>

                <p style={{ lineHeight: 1.5, margin: 0 }}>{alert.reason}</p>

                {alert.reviewedAt ? (
                  <div
                    className="row wrap"
                    style={{
                      gap: "var(--s2)",
                      alignItems: "center",
                      color: "var(--teal-ink)",
                      fontSize: "0.9rem",
                    }}
                  >
                    <span>✓ بررسی شد در {faDigits(alert.reviewedAt.slice(0, 10))}</span>
                    {alert.reviewNote && <span>— یادداشت: «{alert.reviewNote}»</span>}
                  </div>
                ) : (
                  <div style={{ marginTop: "var(--s1)" }}>
                    {isReviewing ? (
                      <div className="row wrap" style={{ gap: "var(--s2)", alignItems: "center" }}>
                        <input
                          type="text"
                          className="input flex-1"
                          placeholder="یادداشت بررسی (مثلاً دلیل افت، هماهنگی با سازمان، و ...)"
                          value={reviewNote}
                          onChange={(e) => {
                            setReviewNote(e.target.value);
                          }}
                        />
                        <Button
                          type="button"
                          onClick={() => {
                            reviewAction.run({ alert, note: reviewNote });
                          }}
                          disabled={reviewAction.busy}
                        >
                          {reviewAction.busy ? "در حال ثبت..." : "تأیید و ثبت بررسی"}
                        </Button>
                        <button
                          type="button"
                          className="btn btn-subtle"
                          onClick={() => {
                            setReviewingId(null);
                          }}
                        >
                          انصراف
                        </button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        onClick={() => {
                          setReviewingId(alert.id);
                          setReviewNote("");
                        }}
                      >
                        بررسی شد
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
